export type ImportKind = 'sales' | 'products';
export type ImportStatus =
  | 'awaiting_mapping'
  | 'queued'
  | 'processing'
  | 'completed'
  | 'completed_with_errors'
  | 'cancelled'
  | 'failed';

export interface ImportJobTotals {
  rows: number;
  processed: number;
  succeeded: number;
  failed: number;
  needs_review: number;
  skipped: number;
}

export interface ImportJob {
  id: string;
  businessId: string;
  data: {
    kind: ImportKind;
    status: ImportStatus;
    file_storage_ref: string;
    file_name?: string | null;
    encoding_used: 'utf-8' | 'windows-1252';
    separator_used: ',' | ';' | '\t';
    encoding_fallback: boolean;
    separator_fallback: boolean;
    source_columns: string[];
    column_mapping: Record<string, string>;
    sample_rows: Record<string, string>[];
    totals: ImportJobTotals;
    progress_percent: number;
    cancel_requested?: boolean;
    started_at?: string | null;
    finished_at?: string | null;
    fatal_error?: Record<string, unknown> | null;
  };
  createdAt: string;
  updatedAt: string;
}

export type ImportRowStatus = 'succeeded' | 'failed' | 'needs_review' | 'skipped';

export interface ImportJobRow {
  id: string;
  data: {
    row_number: number;
    status: ImportRowStatus;
    raw_row: Record<string, string>;
    mapped_row?: Record<string, unknown> | null;
    error_code?: string | null;
    error_message?: string | null;
    /** Set by backend enrichment via describeErrorCode. */
    error_title_pt_br?: string | null;
    error_hint_pt_br?: string | null;
    /** Affected field name (when mapper identifies it). */
    field_label?: string | null;
    /** Raw technical detail (PG code/constraint/detail). */
    debug_info?: string | null;
    divergent_fields?: string[] | null;
  };
}

export interface ImportJobRowsResponse {
  data: ImportJobRow[];
  meta: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface ConfirmMappingPayload {
  mapping: Record<string, string>;
  update_policy?: { default: 'skip' | 'update' | 'create_only' | 'fill_empty' };
  ambiguity_policy?: 'skip_row' | 'pick_oldest' | 'pick_newest_active';
  name_divergence_policy?: 'keep_existing' | 'update' | 'needs_review';
  on_duplicate?: 'skip' | 'update' | 'fail';
}

export interface CanonicalField {
  key: string;
  label: string;
  /** Hard requirement — falha se não mapeado. */
  required?: boolean;
  /** Pelo menos um campo do mesmo grupo deve estar mapeado. */
  requiredOneOf?: string;
  /** Valores aceitos descritos pra usuário (mostrado em tooltip + tabela ref). */
  acceptedValues?: string;
  /** Dica curta complementar mostrada na tabela de referência. */
  hint?: string;
}

export const SALES_CANONICAL_FIELDS: ReadonlyArray<CanonicalField> = [
  {
    key: 'email',
    label: 'Email do contato',
    requiredOneOf: 'channel',
    acceptedValues: 'Endereço de email válido (ex: nome@empresa.com)',
    hint: 'Necessário pelo menos um entre Email ou Telefone.',
  },
  {
    key: 'phone',
    label: 'Telefone do contato',
    requiredOneOf: 'channel',
    acceptedValues: 'Formato E.164 ou nacional (ex: +5511999999999)',
    hint: 'Necessário pelo menos um entre Email ou Telefone.',
  },
  { key: 'first_name', label: 'Nome do contato', acceptedValues: 'Texto livre' },
  { key: 'last_name', label: 'Sobrenome do contato', acceptedValues: 'Texto livre' },
  {
    key: 'product_name',
    label: 'Nome do produto',
    required: true,
    acceptedValues: 'Nome exato cadastrado no catálogo',
  },
  { key: 'quantity', label: 'Quantidade', acceptedValues: 'Número inteiro ≥ 1 (padrão 1)' },
  { key: 'unit_price', label: 'Preço unitário', acceptedValues: 'Decimal (ex: 199.90 ou 199,90)' },
  { key: 'currency', label: 'Moeda', acceptedValues: 'ISO-4217 3 letras: BRL, USD, EUR…' },
  {
    key: 'billing_interval',
    label: 'Intervalo de cobrança',
    acceptedValues: 'one_time | monthly | quarterly | semiannual | annual | custom',
  },
  { key: 'coupon_code', label: 'Código do cupom', acceptedValues: 'Código exato cadastrado' },
  { key: 'total', label: 'Total da venda', acceptedValues: 'Decimal (ex: 199.90). Se vazio: calc qty × unit_price' },
  { key: 'valid_until', label: 'Validade (orçamento)', acceptedValues: 'Data ISO (YYYY-MM-DD) ou DD/MM/YYYY' },
  { key: 'external_id', label: 'ID externo (provedor)', acceptedValues: 'Texto livre — identificador no provedor' },
  { key: 'external_provider', label: 'Provedor externo', acceptedValues: 'Nome do provedor (hotmart, kiwify, stripe…)' },
];

export const PRODUCTS_CANONICAL_FIELDS: ReadonlyArray<CanonicalField> = [
  {
    key: 'name',
    label: 'Nome do produto',
    required: true,
    acceptedValues: 'Texto único por business (ex: Curso de Excel Avançado)',
  },
  { key: 'description', label: 'Descrição', acceptedValues: 'Texto livre' },
  {
    key: 'type',
    label: 'Tipo',
    acceptedValues: 'course | service | physical | saas | access | mentoring | ebook | other',
  },
  { key: 'amount', label: 'Preço', acceptedValues: 'Decimal (ex: 199.90 ou 199,90)' },
  { key: 'currency', label: 'Moeda', acceptedValues: 'ISO-4217: BRL, USD, EUR…' },
  {
    key: 'billing_interval',
    label: 'Intervalo de cobrança',
    acceptedValues: 'one_time | monthly | quarterly | semiannual | annual | custom',
  },
  {
    key: 'custom_interval_value',
    label: 'Intervalo customizado (valor)',
    acceptedValues: 'Número inteiro ≥ 1 — obrigatório quando billing_interval=custom',
  },
  {
    key: 'custom_interval_unit',
    label: 'Intervalo customizado (unidade)',
    acceptedValues: 'day | week | month — obrigatório quando billing_interval=custom',
  },
  { key: 'external_provider', label: 'Provedor externo', acceptedValues: 'Nome do provedor' },
  { key: 'external_id', label: 'ID externo', acceptedValues: 'Identificador no provedor' },
];
